import { useState } from 'react';
import { HeroSectionVideoRoot,
	FeatureText,
	Heading,
	MainText, 
	Subtext, 
	ButtonWrapper, 
	TextSection, 
	VideoSection,
} from './HeroSectionVideo.styled';
import { Button } from '../button/Button';
import { SectionVideo } from './VideoSection';
import styled from 'styled-components';
import { css } from 'styled-components';
import { Modal } from '../modal';
import { UpgradePagesModal } from './UpgradePagesModal';
import { LinkButton } from '../button/LinkButton';

declare global {
	interface Window {
		hbspt: {
			forms: {
				create: (options: {
					region: string;
					portalId: string;
					target: string;
					formId: string;
					onFormReady?: (form: unknown) => void;
					onFormSubmit?: () => void;
				}) => void;
			};
		};
	}
}

interface Subscription {
	name: string;
	uuid: string;
}

interface UpgradePage {
	featureName: string;
	slug: string;
	hubspotEmail: string;
	hubspotCountry: string;
	hubspotIndustry: string;
	hubspotLink: string;
	upgradePageLink: string;
	subscription: Subscription;
	alreadyTried: boolean;
}

interface ModalConfig {
	modalTitle: string;
	modalSubtitle: string;
	modalContentHeader: string;
	tiles: Array<{
		icon: string;
		tileTitle: string;
		tileDescription: string;
		isFullWidth: boolean;
		isHorizontal: boolean;
	}>;
}

interface Props {
	heading: string;
	mainText: string;
	subtext: string;
	videoUrl: string;
	isAdmin: boolean;
	upgradePage: UpgradePage;
	modal: ModalConfig;
	primaryButtonLabel: string;
	secondaryButtonLabel: string;
	position?: 'left' | 'right';
	showBorder?: boolean;
	isVimeo?: boolean;
	primaryButtonLabelTried: string;
	primaryButtonLabelModal: string;
	secondaryButtonLabelModal: string;
	hubspotEmail: string;
	hubspotCountry: string;
	hubspotIndustry: string;
	hubspotLink: string;
	upgradePageLink: string;
	onFeatureTrialStart?: () => Promise<void>;
	isSubmitting?: boolean;
}

interface ModalProps extends React.ComponentProps<typeof Modal> {
	$hasParent: boolean;
}

const StyledModal = styled(Modal)<ModalProps>`
	${({ $hasParent }) => $hasParent ? css`
	position: absolute;
	` : ''}
`;

export const HeroSectionVideo = ({ 
	heading, 
	mainText, 
	subtext, 
	videoUrl, 
	isAdmin, 
	upgradePage,
	modal,
	primaryButtonLabel, 
	secondaryButtonLabel,
	position,
	showBorder = false,
	isVimeo = true,
	primaryButtonLabelTried,
	primaryButtonLabelModal,
	secondaryButtonLabelModal,
	hubspotEmail,
	hubspotLink,
	upgradePageLink,
	onFeatureTrialStart = async () => {
		console.warn('No feature trial start handler provided');
	},
	isSubmitting = false,
}: Props) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const prefilledHubspotLink = `${hubspotLink}?email=${encodeURIComponent(hubspotEmail)}`;

	const renderButtons = () => (
		<ButtonWrapper>
			{upgradePage.alreadyTried ? (
				<LinkButton 
					variant="primary" 
					href={upgradePageLink} 
					style={{ padding: '10px 10px' }}
				>
					{primaryButtonLabelTried}
				</LinkButton>
			) : (
				<>
					<Button variant="primary" onClick={() => setIsModalOpen(true)}>
						{primaryButtonLabel}
					</Button>
	
					<LinkButton 
						variant="secondary" 
						href={prefilledHubspotLink} 
						target="_blank"
						rel="noopener noreferrer"
						style={{ padding: '10px 10px' }}
					>
						{secondaryButtonLabel}
					</LinkButton>
				</>
			)}
		</ButtonWrapper>
	);

	return (
		<>
			<HeroSectionVideoRoot position={position}>
				<TextSection>
					<Heading>
						<span>{heading} <FeatureText>{upgradePage.featureName}</FeatureText></span>
					</Heading>
					<MainText $isAdmin={isAdmin}>
						{mainText}
					</MainText>
					{(isAdmin) && renderButtons()}
					<Subtext>
						{subtext}
					</Subtext>
				</TextSection>
				<VideoSection>
					<SectionVideo
						videoUrl={videoUrl}
						showBorder={showBorder}
						isVimeo={isVimeo}
					/>
				</VideoSection>
			</HeroSectionVideoRoot>

			{isModalOpen && (
				<StyledModal
					isOpen={isModalOpen}
					onRequestClose={() => setIsModalOpen(false)}
					$hasParent={false}
				>
					<UpgradePagesModal
						modalTitle={modal.modalTitle}
						modalSubtitle={modal.modalSubtitle}
						modalContentHeader={modal.modalContentHeader}
						tiles={modal.tiles}
						primaryButtonLabelModal={primaryButtonLabelModal}
						secondaryButtonLabelModal={secondaryButtonLabelModal}
						onClose={() => setIsModalOpen(false)}
						onPrimaryButtonClick={onFeatureTrialStart}
						isSubmitting={isSubmitting}
					/>
				</StyledModal>
			)}
		</>
	);
};
